import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Checkbox,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import OrderManagerTab from '../components/order-manager/OrderManagerTab';
import { getOrderManagerCounts } from '../services/api';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

const ORDER_TABS = [
  { id: 'to-be-sent', label: 'To Be Sent' },
  { id: 'pending-signature', label: 'Pending Physician Signature' },
  // Id stays 'received' to preserve deep links and the STATUS_MAP wire format.
  { id: 'received', label: 'Completed' },
] as const;

type TabId = (typeof ORDER_TABS)[number]['id'];

const Orders: React.FC = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );

  const [searchParams] = useSearchParams();
  const highlightOrderId = searchParams.get('highlightOrder') ?? undefined;
  const tabParam = searchParams.get('tab');
  const initialTabId: TabId = ORDER_TABS.some(t => t.id === tabParam)
    ? (tabParam as TabId)
    : ORDER_TABS[0].id;

  const [activeTab, setActiveTab] = useState<TabId>(initialTabId);
  const [mountedTabIds, setMountedTabIds] = useState<Set<TabId>>(
    () => new Set([initialTabId])
  );
  const [refetchKey, setRefetchKey] = useState(0);
  const [hideImportedPatients, setHideImportedPatients] = useState(true);
  const [tabCounts, setTabCounts] = useState<Record<TabId, number>>({
    'to-be-sent': 0,
    'pending-signature': 0,
    received: 0,
  });

  const fetchCounts = useCallback(async () => {
    const res = await getOrderManagerCounts({
      query: { hide_imported_patients: hideImportedPatients },
    });
    if (res.data) {
      setTabCounts({
        'to-be-sent': res.data.to_be_sent,
        'pending-signature': res.data.pending_signature,
        received: res.data.completed,
      });
    }
  }, [hideImportedPatients]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, refetchKey]);
  const location = useLocation();
  const initialKeyRef = useRef(location.key);

  // Refetch order lists on any navigation back to this page.
  // location.key is unique per navigation entry, so it changes even when returning
  // to the same path (handles component unmount/remount after signing an order).
  useEffect(() => {
    if (location.key === initialKeyRef.current) return;
    setRefetchKey(k => k + 1);
  }, [location.key]);

  const handleTabChange = useCallback((value: string) => {
    const id = value as TabId;
    setActiveTab(id);
    setMountedTabIds(prev => new Set(prev).add(id));
  }, []);

  const hasAccess = hasPermission('order-manager:view');

  if (!hasAccess) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access the Order Manager. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold'>Order Manager</h1>
        <p className='text-muted-foreground text-sm'>
          Track orders through sending, physician signature, and return
        </p>
        <label className='mt-3 flex items-center gap-2 text-sm font-medium'>
          <Checkbox
            checked={hideImportedPatients}
            onCheckedChange={checked =>
              setHideImportedPatients(checked === true)
            }
          />
          Filter out imported patients
        </label>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <TabsList className='flex h-auto min-h-8 w-fit gap-0.5 rounded-md border border-border bg-muted/40 p-1'>
          {ORDER_TABS.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className='px-3 py-1.5 text-xs font-medium whitespace-nowrap data-[state=active]:shadow-none sm:text-sm'
            >
              {tab.label}
              {tabCounts[tab.id] > 0 && (
                <span className='ml-1 opacity-75'>({tabCounts[tab.id]})</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <Card>
          <CardContent className='py-0'>
            {ORDER_TABS.map(tab => (
              <TabsContent key={tab.id} value={tab.id} className='m-0'>
                {mountedTabIds.has(tab.id) ? (
                  <OrderManagerTab
                    tabId={tab.id}
                    refetchKey={refetchKey}
                    hideImportedPatients={hideImportedPatients}
                    onAfterRefetch={fetchCounts}
                    highlightOrderId={
                      tab.id === activeTab ? highlightOrderId : undefined
                    }
                  />
                ) : null}
              </TabsContent>
            ))}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
};

export default Orders;
